import { useQuery } from '@tanstack/react-query';
import {
  getKIDContentData,
  getKIDsGeneratorOverviewData,
  getKIDsFigureData,
  getKIDsReviewData,
} from './api.routes';
import FastAPIInstance from '../../../../../services/risksystem3-public-api';
import { KidComparisonEntry } from '../types/KidsData.types';

export function useKIDsOverviewData(clientId: string | undefined) {
  return useQuery({
    queryKey: ['kids-overview-data'],
    queryFn: () => getKIDsGeneratorOverviewData(clientId!),
    enabled: !!clientId, // Ensure clientId is defined before fetching
  });
}

export function useKIDsContentData(
  clientId: string | null,
  fundNameInternal: string | null,
  isin: string | null,
  documentLanguage: string | null,
  version?: string | null,
) {
  return useQuery({
    queryKey: [
      'kids-content-data',
      clientId,
      fundNameInternal,
      isin,
      documentLanguage,
      version,
    ],
    queryFn: () =>
      getKIDContentData(
        clientId!,
        fundNameInternal!,
        isin!,
        documentLanguage!,
        version,
      ), // Enabled ensures all parameters are present
    enabled: !!clientId && !!fundNameInternal && !!isin && !!documentLanguage,
  });
}

export function useKIDsReviewData(
  clientId: string | null,
  fundId: string | null,
  isin: string | null,
  documentLanguage: string | null,
  targetVersion: string | null,
  comparisonVersion: string | null,
) {
  return useQuery({
    queryKey: [
      'kids-review-data',
      fundId,
      isin,
      documentLanguage,
      targetVersion,
      comparisonVersion,
    ],
    queryFn: () =>
      getKIDsReviewData(
        clientId!,
        fundId!,
        isin!,
        documentLanguage!,
        targetVersion!,
        comparisonVersion!,
      ), // Enabled ensures all parameters are present
    enabled:
      !!clientId &&
      !!fundId &&
      !!isin &&
      !!documentLanguage &&
      !!targetVersion &&
      !!comparisonVersion,
  });
}

export function useKIDsFigureData(
  clientId: string | null,
  fundId: string | null,
  shareClass: string | null,
) {
  return useQuery({
    queryKey: ['kids-figure-data', fundId, shareClass],
    queryFn: () => getKIDsFigureData(clientId!, fundId!, shareClass!),
    enabled: !!fundId && !!shareClass && !!clientId,
  });
}

export function useKIDSDocumentFigures(clientId: string, fundids?: string[]) {
  // Only include fundids in the query key if they are provided
  const queryKey = fundids?.length
    ? ['kids-document-figures', clientId, { fundids }]
    : ['kids-document-figures', clientId];

  return useQuery({
    queryKey,
    queryFn: () => getKidsDocumentFigures(clientId, fundids),
    enabled: !!clientId,
    staleTime: 1000 * 60 * 30, // 30mins
  });
}

export function useKIDSPublishedCurrentFigures(client_id: string) {
  return useQuery({
    queryKey: ['kids-published-figures', client_id],
    queryFn: () => getKidsCurrentPublishedFigures(client_id),
    enabled: !!client_id,
    staleTime: 1000 * 60 * 30, // 30mins
  });
}

export type KidDocumentFigure = {
  SRI: number;
  monthly_return: number;
  date: string; // ISO format, e.g., "2025-05-30"
  share_class: string;
  fund_id_string: string;
};

export const getKidsDocumentFigures = async (
  clientId: string,
  fundIds?: string[],
) => {
  const params: Record<string, any> = {};

  // Only add fundIds if they exist and have items
  if (fundIds && fundIds.length > 0) {
    params.fundIds = fundIds;
  }

  return (
    await FastAPIInstance.get<KidDocumentFigure[]>(
      `v1/raidr/document_kid_figures/${clientId}`,
      {
        params,
        // This paramsSerializer is the key to getting the desired URL format
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          // Iterate over the keys in the params object
          for (const key of Object.keys(params)) {
            const value = params[key];
            if (Array.isArray(value)) {
              // If the value is an array, append each item individually with the same key
              value.forEach((item) => searchParams.append(key, item));
            } else {
              // For non-array values, just append normally
              searchParams.append(key, value);
            }
          }
          return searchParams.toString();
        },
      },
    )
  ).data;
};

export const getKidsCurrentPublishedFigures = async (clientId: string) => {
  return (
    await FastAPIInstance.get<KidComparisonEntry[]>(
      `v1/raidr/current_and_published_kid_figures/${clientId}`,
    )
  ).data;
};
